/**
 * Brand marquee (sections/cz-brand-marquee.liquid).
 * Repeats the brands until one copy is wider than the screen, then adds a second copy so the
 * CSS animation (translateX to -50%) loops without a gap. Speed is in px/s; hover or touch pauses it.
 */
class CourtzoneMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cz-marquee__track');
    this.group = this.querySelector('.cz-marquee__group');
    if (!this.track || !this.group || this.group.children.length === 0) return;

    this.originals = [...this.group.children];
    this.#build();

    this.resizeObserver = new ResizeObserver(() => this.#build());
    this.resizeObserver.observe(this);

    // Logo widths are known only after they load.
    this.group.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', () => this.#build(), { once: true });
    });

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('pointerdown', this.#pause);
    this.addEventListener('pointerup', this.#resume);
    this.addEventListener('pointercancel', this.#resume);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('pointerdown', this.#pause);
    this.removeEventListener('pointerup', this.#resume);
    this.removeEventListener('pointercancel', this.#resume);
  }

  #build() {
    const { track, group } = this;
    if (!track || !group) return;

    // Start from the original brands only.
    track.querySelectorAll('[data-cz-marquee-copy]').forEach((node) => node.remove());

    // Repeat the brands inside the first copy until it is at least as wide as the visible area.
    const visibleWidth = this.getBoundingClientRect().width;
    let guard = 0;
    while (group.scrollWidth < visibleWidth && guard < 20) {
      for (const item of this.originals ?? []) {
        const copy = /** @type {HTMLElement} */ (item.cloneNode(true));
        copy.setAttribute('data-cz-marquee-copy', '');
        copy.setAttribute('aria-hidden', 'true');
        group.append(copy);
      }
      guard += 1;
    }

    // The second copy makes the loop seamless.
    const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
    clone.setAttribute('data-cz-marquee-copy', '');
    clone.setAttribute('aria-hidden', 'true');
    track.append(clone);

    const speed = Number(this.dataset.speed) || 35;
    const width = group.getBoundingClientRect().width;
    if (width > 0) this.style.setProperty('--cz-marquee-duration', `${(width / speed).toFixed(1)}s`);
  }

  #pause = () => this.classList.add('is-paused');

  #resume = () => this.classList.remove('is-paused');
}

if (!customElements.get('cz-marquee')) customElements.define('cz-marquee', CourtzoneMarquee);
